// components/portal/DoctrineView.tsx
'use client';

// The multi-layer context system, role-scoped, rendered as a Notion-style
// workspace. Every user sees "My doctrine" (a personal Instructions file + a
// Memory file); org/tool-doctrine admins also see the Organisation layers.
// System doctrine (the hardcoded knowledge bases) is intentionally NOT surfaced
// here — it lives in the codebase.
//
// All content and authorization come from the /api/doctrine route; this
// component renders whatever the server-scoped manifest returns.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmojiPickerLazy } from '@/components/ui/EmojiPickerLazy';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import { TableKit } from '@tiptap/extension-table';
import { Markdown } from 'tiptap-markdown';
import { Trash2 } from '@/components/ui/lucide-memo';
import type {
  DoctrineDocumentDTO,
  DoctrineMemoryEntryDTO,
  DoctrineSectionContentResponse,
  DoctrineSectionMeta,
  DoctrineWorkspaceManifest,
} from '#types/types';
import { markdownComponents } from './SourceDoctrineModal';
import { DoctrinePageHistory } from './DoctrinePageHistory';
import { toast } from 'sonner';
import { MemoryPane } from '@/components/doctrine/MemoryPane';

// Notion-style emoji picker (emoji-picker-react), client-only (no SSR). Typed
// loosely so the view doesn't couple to the library's strict prop types.

// Pre-made, per-page default icons (Notion-style); users can still change them.
// Keys may be org-scoped (orgroot:<id>, org:<id>:GLOBAL, org:<id>:tool:<TOOL>).
const resolveIcon = (key: string): string => {
  if (key.startsWith('orgroot:')) return '🏢';
  if (key.startsWith('team:')) return '👥'; // per-team doctrine (e.g. auto-generated Studio doctrine)
  if (key.endsWith(':GLOBAL')) return '🏛️';
  if (key.endsWith(':tool:SOURCE')) return '🔍';
  if (key.endsWith(':tool:ANALYST')) return '📊';
  if (key.endsWith(':tool:STUDIO')) return '🎨';
  if (key.endsWith(':tool:SLIDES')) return '📑';
  if (key.endsWith(':tool:MASTER')) return '🤖';
  if (key === 'myroot') return '👤';
  if (key === 'user:instructions') return '📋';
  if (key === 'user:memory') return '🧠';
  return '📄';
};

// Org root pages use the organisation's real logo (served by the app) as the icon.
const orgLogoUrl = (key: string): string | null =>
  key.startsWith('orgroot:') ? `/api/organisation/logo?orgId=${key.slice('orgroot:'.length)}` : null;

// A node's glyph: a user-picked emoji override wins; else the org logo (org roots);
// else the pre-made emoji.
const NodeGlyphImpl = ({ nodeKey, override, px }: { nodeKey: string; override?: string; px: number }) => {
  const logo = orgLogoUrl(nodeKey);
  const imgStyle = useMemo(() => ({ width: px, height: px }), [px]);
  const spanStyle = useMemo(
    () => ({ width: px, fontSize: px >= 40 ? px : Math.round(px * 0.85) }),
    [px],
  );
  if (!override && logo) {
    return <img src={logo} alt="" className="shrink-0 rounded-[4px] object-contain" style={imgStyle} />;
  }
  return (
    <span className="shrink-0 text-center leading-none" style={spanStyle}>
      {override ?? resolveIcon(nodeKey)}
    </span>
  );
};
const NodeGlyph = React.memo(NodeGlyphImpl);
NodeGlyph.displayName = 'NodeGlyph';

// A node in the doctrine page tree. `canAddSub` gates the
// "+" affordance (only document-backed pages and subpages take children);
// `deletable` marks user-created subpages.
type DoctrineNode = {
  key: string;
  title: string;
  canAddSub: boolean;
  deletable: boolean;
  children: DoctrineNode[];
};

const RetryButton = ({ onRetry }: { onRetry: () => void }) => (
  <button
    type="button"
    onClick={onRetry}
    className="mt-3 rounded-[6px] px-2.5 py-1 text-[13px] text-[#8A8A8A] transition-colors hover:bg-white/[0.06] hover:text-[#E8E8E8]"
  >
    Retry
  </button>
);

// Sidebar rows load as shimmering placeholders of varied width (like Notion's
// page tree), so the loading state already reads as "a list of pages".
const SIDEBAR_SKELETON_WIDTHS = ['72%', '58%', '81%', '46%', '67%', '52%'];
const SidebarSkeleton = () => (
  <div className="flex flex-col gap-2 px-1 pt-2">
    {SIDEBAR_SKELETON_WIDTHS.map((w, i) => (
      <div key={i} className="doctrine-skeleton h-[22px]" style={{ width: w }} />
    ))}
  </div>
);

// Content placeholder shaped like a loaded page: a title line, a short subtitle,
// then paragraph lines of uneven width. No spinner, no "Loading…" copy.
const CONTENT_SKELETON_LINES = ['96%', '88%', '92%', '61%', '0', '90%', '84%', '73%'];
const ContentSkeleton = () => (
  <div aria-hidden className="pt-1">
    <div className="doctrine-skeleton h-[38px] w-[52%]" />
    <div className="mb-9 mt-4 doctrine-skeleton h-[15px] w-[24%]" />
    <div className="flex flex-col gap-[14px]">
      {CONTENT_SKELETON_LINES.map((w, i) =>
        w === '0' ? (
          <div key={i} className="h-2" />
        ) : (
          <div key={i} className="doctrine-skeleton h-[14px]" style={{ width: w }} />
        ),
      )}
    </div>
  </div>
);

// ---- Document editor (org / tool / user instructions) ---------------------
// Notion-style rich editor (TipTap): rendered blocks while you type (## → real
// heading), floating selection toolbar, borderless page, debounced autosave.
// STORAGE STAYS MARKDOWN — the API, the agent's READ/UPDATE_DOCTRINE commands
// and the prompt injection all keep consuming the same markdown string.

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

// Static — the remark plugin list never changes across renders.
const REMARK_PLUGINS = [remarkGfm];

const serializeMarkdown = (editor: Editor): string =>
  (editor.storage as { markdown?: { getMarkdown: () => string } }).markdown?.getMarkdown() ?? '';

// Static — never depends on props/state, so a plain module-level function is
// already a stable reference (no useCallback needed).
const preventMouseDown = (e: React.MouseEvent) => e.preventDefault();

const BubbleButtonImpl = ({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onMouseDown={preventMouseDown}
    onClick={onClick}
    className={
      'flex h-7 min-w-7 items-center justify-center rounded-[6px] px-1.5 text-[13px] transition-colors ' +
      (active ? 'bg-white/[0.12] text-[#F0F0F0]' : 'text-[#B5B5B5] hover:bg-white/[0.07] hover:text-[#E8E8E8]')
    }
  >
    {children}
  </button>
);
const BubbleButton = React.memo(BubbleButtonImpl);
BubbleButton.displayName = 'BubbleButton';

// Static icon nodes for the bubble menu buttons — hoisted so each stays the
// same reference across renders (BubbleButton is memoized on its `children`).
const IconHeading2 = <span className="font-semibold">H2</span>;
const IconHeading3 = <span className="font-semibold">H3</span>;
const IconBold = <span className="font-bold">B</span>;
const IconItalic = <span className="italic">i</span>;
const IconStrike = <span className="line-through">S</span>;
const IconCode = <span className="font-mono text-[12px]">{'<>'}</span>;
const IconBulletList = <span className="text-[15px] leading-none">•</span>;
const IconOrderedList = <span className="text-[12px]">1.</span>;
const IconBlockquote = <span className="text-[15px] leading-none">{'"'}</span>;

const BUBBLE_MENU_OPTIONS = { placement: 'top' as const };

// `onFirstEdit`: a page that followed Fater's default is now the organisation's, and the tree's badge
// and Reset button are read from the manifest, so the owner re-reads it once.
const DocumentPane = ({ section, document, onFirstEdit }: { section: DoctrineSectionMeta; document: DoctrineDocumentDTO; onFirstEdit?: () => void }) => {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(document.updatedAt);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mandatoryRef = useRef(document.mandatory);
  // The version this editor started from, advanced by each save; the server refuses a save on top of
  // someone else's, and from then on this editor stops saving rather than overwrite them.
  const baseVersionRef = useRef(document.version);
  const conflictedRef = useRef(false);
  const firstEditReportedRef = useRef(false);

  const supportsMandatory = section.kind === 'ORG' || section.kind === 'TOOL';

  const doSave = useCallback(
    async (content: string) => {
      if (conflictedRef.current) return;
      setStatus('saving');
      try {
        const res = await fetch('/api/doctrine', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            key: section.key,
            content,
            mandatory: supportsMandatory ? mandatoryRef.current : undefined,
            baseVersion: baseVersionRef.current,
          }),
        });
        if (res.status === 409) {
          conflictedRef.current = true;
          setStatus('conflict');
          return;
        }
        if (!res.ok) throw new Error();
        const data: { document: DoctrineDocumentDTO } = await res.json();
        baseVersionRef.current = data.document.version;
        setSavedAt(data.document.updatedAt);
        setStatus('saved');
        if (section.followsDefault && !firstEditReportedRef.current) {
          firstEditReportedRef.current = true;
          onFirstEdit?.();
        }
      } catch {
        setStatus('error');
      }
    },
    [section.key, section.followsDefault, supportsMandatory, onFirstEdit],
  );

  // Debounced autosave, Notion-style — no explicit Save button.
  const scheduleSave = useCallback(
    (content: string) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void doSave(content), 700);
    },
    [doSave],
  );

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      // Tables — the barème rubric renders as a real editable table (StarterKit
      // omits it, so without this the markdown pipes were dropped on load/save).
      TableKit.configure({ table: { resizable: false } }),
      Markdown.configure({ html: false, transformPastedText: true }),
      Placeholder.configure({
        placeholder:
          section.group === 'MY_DOCTRINE'
            ? 'Write standing instructions the agents should always follow for you…'
            : 'Write the doctrine every agent in this scope should follow…',
      }),
    ],
    content: document.content,
    editable: document.editable,
    immediatelyRender: false,
    onUpdate: ({ editor: e }) => {
      scheduleSave(serializeMarkdown(e));
    },
  });

  const toggleHeading2 = useCallback(() => { editor?.chain().focus().toggleHeading({ level: 2 }).run(); }, [editor]);
  const toggleHeading3 = useCallback(() => { editor?.chain().focus().toggleHeading({ level: 3 }).run(); }, [editor]);
  const toggleBold = useCallback(() => { editor?.chain().focus().toggleBold().run(); }, [editor]);
  const toggleItalic = useCallback(() => { editor?.chain().focus().toggleItalic().run(); }, [editor]);
  const toggleStrike = useCallback(() => { editor?.chain().focus().toggleStrike().run(); }, [editor]);
  const toggleCode = useCallback(() => { editor?.chain().focus().toggleCode().run(); }, [editor]);
  const toggleBulletList = useCallback(() => { editor?.chain().focus().toggleBulletList().run(); }, [editor]);
  const toggleOrderedList = useCallback(() => { editor?.chain().focus().toggleOrderedList().run(); }, [editor]);
  const toggleBlockquote = useCallback(() => { editor?.chain().focus().toggleBlockquote().run(); }, [editor]);

  if (!document.editable) {
    return (
      <div className="doctrine-prose pt-1">
        {document.content.trim() ? (
          <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>
            {document.content}
          </ReactMarkdown>
        ) : (
          <div className="text-[14px] text-[#6E6E6E]">Nothing has been written for this layer yet.</div>
        )}
      </div>
    );
  }

  const statusLabel =
    status === 'saving' ? 'Saving…' : status === 'error' ? 'Save failed, retrying on next edit' : status === 'conflict' ? 'Someone else saved this page since you opened it. Reload it to see their version; your last edit was not saved.' : savedAt ? `Edited ${new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : '';

  return (
    <div className="flex flex-col">
      {editor && (
        <BubbleMenu editor={editor} options={BUBBLE_MENU_OPTIONS}>
          <div className="flex items-center gap-0.5 rounded-[8px] border border-white/[0.08] bg-[#252525] p-1 shadow-[0_8px_24px_rgba(0,0,0,0.45)]">
            <BubbleButton active={editor.isActive('heading', { level: 2 })} label="Heading" onClick={toggleHeading2}>
              {IconHeading2}
            </BubbleButton>
            <BubbleButton active={editor.isActive('heading', { level: 3 })} label="Subheading" onClick={toggleHeading3}>
              {IconHeading3}
            </BubbleButton>
            <div className="mx-0.5 h-4 w-px bg-white/[0.1]" />
            <BubbleButton active={editor.isActive('bold')} label="Bold" onClick={toggleBold}>
              {IconBold}
            </BubbleButton>
            <BubbleButton active={editor.isActive('italic')} label="Italic" onClick={toggleItalic}>
              {IconItalic}
            </BubbleButton>
            <BubbleButton active={editor.isActive('strike')} label="Strikethrough" onClick={toggleStrike}>
              {IconStrike}
            </BubbleButton>
            <BubbleButton active={editor.isActive('code')} label="Code" onClick={toggleCode}>
              {IconCode}
            </BubbleButton>
            <div className="mx-0.5 h-4 w-px bg-white/[0.1]" />
            <BubbleButton active={editor.isActive('bulletList')} label="Bulleted list" onClick={toggleBulletList}>
              {IconBulletList}
            </BubbleButton>
            <BubbleButton active={editor.isActive('orderedList')} label="Numbered list" onClick={toggleOrderedList}>
              {IconOrderedList}
            </BubbleButton>
            <BubbleButton active={editor.isActive('blockquote')} label="Quote" onClick={toggleBlockquote}>
              {IconBlockquote}
            </BubbleButton>
          </div>
        </BubbleMenu>
      )}

      <EditorContent editor={editor} className="doctrine-editor" />
      <div className="mt-6 text-[12px] text-[#565656]">{statusLabel}</div>
    </div>
  );
};


// ---- Main view ------------------------------------------------------------

// Static — the emoji picker's preview pane is always disabled.
const EMOJI_PICKER_PREVIEW_CONFIG = { showPreview: false };

// Notion-style page icon: an "Add icon" affordance that opens an emoji picker,
// then shows the chosen emoji with click-to-change / remove. Local-only for now;
// persisted once DoctrineDocument gains an `icon` field.
const PageIcon = ({
  nodeKey,
  override,
  onPick,
  onRemove,
}: {
  nodeKey: string;
  override?: string;
  onPick: (emoji: string) => void;
  onRemove: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggleOpen = useCallback(() => setOpen((o) => !o), []);

  const handleEmojiClick = useCallback(
    (d: { emoji: string }) => {
      onPick(d.emoji);
      setOpen(false);
    },
    [onPick],
  );

  const handleRemoveClick = useCallback(() => {
    onRemove();
    setOpen(false);
  }, [onRemove]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={toggleOpen}
        className="-ml-1 block leading-none transition-transform hover:scale-[1.03]"
        title="Change icon"
      >
        <NodeGlyph nodeKey={nodeKey} override={override} px={78} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-2">
          <EmojiPickerLazy
            theme="dark"
            width={320}
            height={400}
            lazyLoadEmojis
            previewConfig={EMOJI_PICKER_PREVIEW_CONFIG}
            onEmojiClick={handleEmojiClick}
          />
          {override && (
            <button
              type="button"
              onClick={handleRemoveClick}
              className="mt-1 rounded-[6px] px-2 py-1 text-[12px] text-[#8A8A8A] transition-colors hover:bg-white/[0.06] hover:text-[#B5B5B5]"
            >
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// A single row in the sidebar page tree (recursive — renders its own expanded
// children). Memoized: stable props (primitives, the shared `expanded` Set,
// and the parent's already-stable callbacks) let it skip re-render when an
// unrelated part of the tree changes.
const TreeRowImpl = ({
  node,
  depth,
  selectedKey,
  expanded,
  iconOverride,
  onToggleExpand,
  onSelect,
  onRemoveSubpage,
  onAddSubpage,
}: {
  node: DoctrineNode;
  depth: number;
  selectedKey: string | null;
  expanded: Set<string>;
  iconOverride: (key: string) => string | undefined;
  onToggleExpand: (key: string) => void;
  onSelect: (key: string) => void;
  onRemoveSubpage: (key: string) => void;
  onAddSubpage: (key: string) => void;
}) => {
  const isActive = node.key === selectedKey;
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.key);
  const rowStyle = useMemo(() => ({ paddingLeft: 4 + depth * 14 }), [depth]);
  const chevronStyle = useMemo(() => ({ transform: isOpen ? 'rotate(90deg)' : 'none' }), [isOpen]);
  const handleToggle = useCallback(() => onToggleExpand(node.key), [onToggleExpand, node.key]);
  const handleSelect = useCallback(() => onSelect(node.key), [onSelect, node.key]);
  const handleRemove = useCallback(() => onRemoveSubpage(node.key), [onRemoveSubpage, node.key]);
  const handleAddSub = useCallback(() => onAddSubpage(node.key), [onAddSubpage, node.key]);

  return (
    <div>
      <div
        className={
          'group/row flex items-center gap-1 rounded-[6px] pr-1 transition-colors ' +
          (isActive ? 'bg-white/[0.06]' : 'hover:bg-white/[0.035]')
        }
        style={rowStyle}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={handleToggle}
            className="flex h-5 w-4 shrink-0 items-center justify-center text-[#8A8A8A] transition-transform duration-150 hover:text-[#C8C8C8]"
            style={chevronStyle}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            <svg viewBox="0 0 12 12" width="11" height="11" fill="currentColor" aria-hidden="true">
              <path d="M4.5 3.9c0-.5.53-.79.96-.53l3.3 2.03a.62.62 0 0 1 0 1.06l-3.3 2.03a.62.62 0 0 1-.96-.53V3.9Z" />
            </svg>
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <button
          type="button"
          onClick={handleSelect}
          className="flex min-w-0 flex-1 items-center gap-2 py-[6px] text-left"
        >
          <NodeGlyph nodeKey={node.key} override={iconOverride(node.key)} px={17} />
          <span className={'truncate text-[14px] ' + (isActive ? 'text-[#E8E8E8]' : 'text-[#A8A8A8]')}>
            {node.title}
          </span>
        </button>
        {node.deletable && (
          <button
            type="button"
            onClick={handleRemove}
            title="Delete this subpage"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[#7A7A7A] opacity-0 transition hover:bg-white/[0.08] hover:text-[#E06C6C] group-hover/row:opacity-100"
          >
            <Trash2 size={13} />
          </button>
        )}
        {node.canAddSub && (
          <button
            type="button"
            onClick={handleAddSub}
            title="Add a subpage"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[16px] leading-none text-[#7A7A7A] opacity-0 transition hover:bg-white/[0.08] hover:text-[#B5B5B5] group-hover/row:opacity-100"
          >
            +
          </button>
        )}
      </div>
      {isOpen &&
        node.children.map((c) => (
          <TreeRow
            key={c.key}
            node={c}
            depth={depth + 1}
            selectedKey={selectedKey}
            expanded={expanded}
            iconOverride={iconOverride}
            onToggleExpand={onToggleExpand}
            onSelect={onSelect}
            onRemoveSubpage={onRemoveSubpage}
            onAddSubpage={onAddSubpage}
          />
        ))}
    </div>
  );
};
const TreeRow = React.memo(TreeRowImpl);
TreeRow.displayName = 'TreeRow';

// A subpage link shown inline at the top of a page (Notion-style). Memoized:
// the parent passes the stable `setSelectedKey` state setter directly.
const SubpageLinkImpl = ({
  nodeKey,
  title,
  override,
  onSelect,
}: {
  nodeKey: string;
  title: string;
  override?: string;
  onSelect: (key: string) => void;
}) => {
  const handleClick = useCallback(() => onSelect(nodeKey), [onSelect, nodeKey]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className="group/link flex w-fit items-center gap-2.5 rounded-[6px] px-1.5 py-1.5 text-left transition-colors hover:bg-white/[0.04]"
    >
      <NodeGlyph nodeKey={nodeKey} override={override} px={20} />
      <span className="truncate text-[15px] text-[#C8C8C8] underline decoration-[#3A3A3A] underline-offset-[3px] transition-colors group-hover/link:decoration-[#6E6E6E]">
        {title}
      </span>
    </button>
  );
};
const SubpageLink = React.memo(SubpageLinkImpl);
SubpageLink.displayName = 'SubpageLink';

/** `rootKey` shows one branch of the tree (the Slides tool doctrine inside Slides); `manifestQuery` rides
 *  on the tree request, e.g. asking the server to keep that tool's system pages present first.
 *  `fullWidth` is Notion's full-width page, for a view embedded where no chat sits beside it. */
export const DoctrineView = ({ rootKey, manifestQuery = '', fullWidth = false }: { rootKey?: string; manifestQuery?: string; fullWidth?: boolean } = {}) => {
  const manifestUrl = `/api/doctrine${manifestQuery}`;
  const [manifest, setManifest] = useState<DoctrineWorkspaceManifest | null>(null);
  const [manifestError, setManifestError] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [content, setContent] = useState<DoctrineSectionContentResponse | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState(false);
  // Optimistic per-page icon overrides, keyed by section key — the persisted
  // value rides in each section's `icon` (see iconOverride); this holds the icon
  // the user just picked so it shows before the manifest refetch lands.
  const [pageIcons, setPageIcons] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const requestSeq = useRef(0);

  const sectionByKey = useMemo(
    () => new Map((manifest?.sections ?? []).map((s) => [s.key, s])),
    [manifest],
  );
  // The emoji override for a page: an optimistic pick wins, else the stored icon.
  // Undefined ⇒ NodeGlyph falls back to the org logo / pre-made default.
  const iconOverride = useCallback(
    (key: string): string | undefined => pageIcons[key] ?? sectionByKey.get(key)?.icon ?? undefined,
    [pageIcons, sectionByKey],
  );

  const toggleExpand = useCallback((key: string) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const loadManifest = useCallback(async () => {
    setManifestError(false);
    setManifest(null);
    try {
      const res = await fetch(manifestUrl, { cache: 'no-store' });
      if (!res.ok) throw new Error(`Manifest failed (${res.status})`);
      const data: DoctrineWorkspaceManifest = await res.json();
      setManifest(data);
      // Keep a still-valid selection across refetches, but do NOT auto-open a
      // page on first load — landing on an arbitrary org's (often empty) doctrine
      // is confusing. Start on a neutral empty state; the user picks a page.
      // A single branch (rootKey) is not arbitrary: it opens on its first system page.
      // The manifest leaves out a branch the caller has no rights on: nothing to open then.
      const branchShown = !rootKey || data.sections.some((s) => s.key === rootKey);
      const opening = rootKey && branchShown ? data.sections.find((s) => s.parentKey === rootKey && s.systemKey)?.key ?? rootKey : null;
      if (rootKey) setExpanded((s) => new Set(s).add(rootKey));
      setSelectedKey((prev) => (prev && data.sections.some((s) => s.key === prev) ? prev : opening));
    } catch {
      setManifestError(true);
    }
  }, [manifestUrl, rootKey]);

  // Quietly re-pull the manifest without tearing down the tree (keeps the
  // sidebar/selection stable after a subpage/icon mutation).
  // The plain tree: `manifestQuery` asks the server to seed pages first, which the first load has done.
  const refreshManifest = useCallback(async () => {
    try {
      const res = await fetch('/api/doctrine', { cache: 'no-store' });
      if (!res.ok) return;
      setManifest(await res.json());
    } catch {
      /* keep the current tree on a transient failure */
    }
  }, []);

  const addSubpage = useCallback(
    async (parentKey: string) => {
      try {
        const res = await fetch('/api/doctrine/pages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ parentKey, title: 'Untitled' }),
        });
        if (!res.ok) return;
        const { section } = (await res.json()) as { section: DoctrineSectionMeta };
        await refreshManifest();
        setExpanded((s) => new Set(s).add(parentKey));
        setSelectedKey(section.key);
      } catch {
        /* no-op: leave the tree unchanged on failure */
      }
    },
    [refreshManifest],
  );

  const removeSubpage = useCallback(
    async (key: string) => {
      const parentKey = sectionByKey.get(key)?.parentKey ?? null;
      try {
        await fetch(`/api/doctrine/pages?id=${encodeURIComponent(key.slice('doc:'.length))}`, {
          method: 'DELETE',
        });
      } catch {
        /* fall through to refetch either way */
      }
      // If the open page (or an ancestor of it) was deleted, fall back to the parent.
      setSelectedKey((prev) => (prev === key ? parentKey : prev));
      await refreshManifest();
    },
    [refreshManifest, sectionByKey],
  );

  // Persist (or clear) a page icon. Fixed layers + subpages store it on their
  // row; pure containers/memory have no row, so those stay optimistic-only.
  const persistableIcon = (key: string): boolean =>
    key.startsWith('doc:') || key.startsWith('org:') || key === 'user:instructions';

  const saveIcon = useCallback(
    async (key: string, emoji: string | null) => {
      setPageIcons((m) => {
        const next = { ...m };
        if (emoji) next[key] = emoji;
        else delete next[key];
        return next;
      });
      if (!persistableIcon(key)) return;
      try {
        await fetch('/api/doctrine/pages', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key, icon: emoji }),
        });
        await refreshManifest();
      } catch {
        /* keep the optimistic icon on a transient failure */
      }
    },
    [refreshManifest],
  );

  // Toggle whether a subpage's content is injected into the agents' context.
  const toggleContributes = useCallback(
    async (key: string, next: boolean) => {
      try {
        await fetch('/api/doctrine/pages', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key, contributesToContext: next }),
        });
        await refreshManifest();
      } catch {
        /* no-op */
      }
    },
    [refreshManifest],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; the fetch settles its own state, changing when it does is a behavioural change
    void loadManifest();
  }, [loadManifest]);

  const loadSection = useCallback(async (key: string) => {
    const seq = ++requestSeq.current;
    setContent(null);
    setContentError(false);
    setContentLoading(true);
    try {
      const res = await fetch(`/api/doctrine?key=${encodeURIComponent(key)}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`Content failed (${res.status})`);
      const data: DoctrineSectionContentResponse = await res.json();
      if (seq !== requestSeq.current) return;
      setContent(data);
      setContentLoading(false);
    } catch {
      if (seq !== requestSeq.current) return;
      setContentError(true);
      setContentLoading(false);
    }
  }, []);

  useEffect(() => {
    // Container roots ("My doctrine", org roots) are synthetic — no server
    // section to fetch. Every document/subpage (incl. doc:<id>) loads.
    if (selectedKey && !selectedKey.startsWith('orgroot:') && selectedKey !== 'myroot')
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; the fetch settles its own state, changing when it does is a behavioural change
      void loadSection(selectedKey);
  }, [selectedKey, loadSection]);

  // When the Master agent writes a doctrine file (UPDATE_DOCTRINE command), the
  // handler broadcasts this event so an open view refreshes what it shows.
  useEffect(() => {
    const onUpdated = (e: Event) => {
      const key = (e as CustomEvent).detail?.key;
      if (typeof key === 'string' && key === selectedKey) void loadSection(key);
    };
    window.addEventListener('doctrine:updated', onUpdated);
    return () => window.removeEventListener('doctrine:updated', onUpdated);
  }, [selectedKey, loadSection]);

  // The page tree is built entirely from the manifest's parentKey links — org
  // roots and "My doctrine" are containers; documents, memory, and user-created
  // subpages hang beneath them. Subpages (and document pages) accept children;
  // containers and the Memory list do not.
  const tree = useMemo<DoctrineNode[]>(() => {
    if (!manifest) return [];
    const byParent = new Map<string | null, DoctrineSectionMeta[]>();
    for (const s of manifest.sections) {
      const pk = s.parentKey ?? null;
      const list = byParent.get(pk) ?? [];
      list.push(s);
      byParent.set(pk, list);
    }
    // A system page is a chapter the system keeps: never a folder, never deleted.
    const canAddSub = (s: DoctrineSectionMeta): boolean =>
      s.contentType === 'document' && !s.container && !s.systemKey;
    const build = (parentKey: string | null): DoctrineNode[] =>
      (byParent.get(parentKey) ?? []).map((s) => ({
        key: s.key,
        title: s.title,
        canAddSub: canAddSub(s),
        deletable: Boolean(s.isSubpage && !s.systemKey),
        children: build(s.key),
      }));
    if (!rootKey) return build(null);
    const branch = manifest.sections.find((s) => s.key === rootKey);
    return branch ? [{ key: branch.key, title: branch.title, canAddSub: canAddSub(branch), deletable: false, children: build(branch.key) }] : [];
  }, [manifest, rootKey]);

  const activeMeta = manifest?.sections.find((s) => s.key === selectedKey) ?? null;
  const isSynthetic = activeMeta?.container ?? false;
  const activeSection = activeMeta && !activeMeta.container ? activeMeta : null;
  const syntheticTitle = activeMeta?.title ?? 'Untitled';

  const handleRetryManifest = useCallback(() => { void loadManifest(); }, [loadManifest]);

  // Which page's history panel is open: a panel toggle, pure view state.
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  const handleToggleHistory = useCallback(() => {
    setHistoryKey((prev) => (prev === selectedKey ? null : selectedKey));
  }, [selectedKey]);
  // A restore or a reset rewrote the page on the server: re-read it and the tree (its badges).
  const handlePageRewritten = useCallback(() => {
    setHistoryKey(null);
    if (selectedKey) void loadSection(selectedKey);
    void refreshManifest();
  }, [selectedKey, loadSection, refreshManifest]);
  const handleResetToDefault = useCallback(async () => {
    if (!selectedKey) return;
    const res = await fetch('/api/doctrine/pages', { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: selectedKey, reset: true }) }).catch(() => null);
    if (res?.ok) handlePageRewritten();
    else toast.error('The page could not be put back on its default. Nothing was changed.');
  }, [selectedKey, handlePageRewritten]);

  const handleRetrySection = useCallback(() => {
    if (selectedKey) void loadSection(selectedKey);
  }, [selectedKey, loadSection]);

  const handleSyntheticIconPick = useCallback(
    (emoji: string) => {
      if (selectedKey) void saveIcon(selectedKey, emoji);
    },
    [selectedKey, saveIcon],
  );
  const handleSyntheticIconRemove = useCallback(() => {
    if (selectedKey) void saveIcon(selectedKey, null);
  }, [selectedKey, saveIcon]);

  const handleActiveSectionIconPick = useCallback(
    (emoji: string) => {
      if (activeSection) void saveIcon(activeSection.key, emoji);
    },
    [activeSection, saveIcon],
  );
  const handleActiveSectionIconRemove = useCallback(() => {
    if (activeSection) void saveIcon(activeSection.key, null);
  }, [activeSection, saveIcon]);

  const handleToggleContributesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (activeSection) void toggleContributes(activeSection.key, e.target.checked);
    },
    [activeSection, toggleContributes],
  );

  // Notion-style: a page shows its subpages inline as clickable links.
  const renderSubpageLinks = (parentKey: string): React.ReactNode => {
    const kids =
      manifest?.sections
        .filter((s) => s.parentKey === parentKey)
        .map((s) => ({ key: s.key, title: s.title, override: iconOverride(s.key) })) ?? [];
    if (!kids.length) return null;
    return (
      <div className="my-3 flex flex-col gap-0.5">
        {kids.map((k) => (
          <SubpageLink key={k.key} nodeKey={k.key} title={k.title} override={k.override} onSelect={setSelectedKey} />
        ))}
      </div>
    );
  };

  return (
    <div className="flex h-full w-full overflow-hidden bg-black">
      {/* Left pane: role-scoped page tree (Notion-style) */}
      <aside className="w-[260px] shrink-0 overflow-y-auto border-r border-[#232323] bg-black px-2 py-4">
        {manifestError ? (
          <div className="px-2 pt-2">
            <div className="text-[13px] text-[#909090]">Failed to load doctrine.</div>
            <RetryButton onRetry={handleRetryManifest} />
          </div>
        ) : manifest === null ? (
          <SidebarSkeleton />
        ) : (
          <nav className="flex flex-col gap-0.5">
            {tree.map((node) => (
              <TreeRow
                key={node.key}
                node={node}
                depth={0}
                selectedKey={selectedKey}
                expanded={expanded}
                iconOverride={iconOverride}
                onToggleExpand={toggleExpand}
                onSelect={setSelectedKey}
                onRemoveSubpage={removeSubpage}
                onAddSubpage={addSubpage}
              />
            ))}
          </nav>
        )}
      </aside>

      {/* Page: Notion-style, left-aligned, no header chrome */}
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
        {/* Left-aligned page (no centered column), so content sits beside the
            tree instead of floating in the middle with a dead left band. */}
        <div className={`w-full py-14 ${fullWidth ? 'px-24' : 'max-w-[860px] pl-20 pr-16'}`}>
          {rootKey && manifest && tree.length === 0 ? (
            <div className="flex h-[60vh] flex-col items-center justify-center text-center doctrine-fade-in">
              <div className="text-[15px] text-[#8A8A8A]">Only your organisation&apos;s administrators and its named experts can see these pages.</div>
            </div>
          ) : selectedKey === null ? (
            <div className="flex h-[60vh] flex-col items-center justify-center text-center doctrine-fade-in">
              <div className="mb-4 text-[40px] opacity-60">📖</div>
              <div className="text-[15px] text-[#8A8A8A]">Select a page from the sidebar to view or edit it.</div>
            </div>
          ) : isSynthetic ? (
            <div key={selectedKey ?? 'syn'} className="group/page doctrine-fade-in">
              <div className="mb-2 min-h-[28px]">
                <PageIcon
                  nodeKey={selectedKey ?? ''}
                  override={iconOverride(selectedKey ?? '')}
                  onPick={handleSyntheticIconPick}
                  onRemove={handleSyntheticIconRemove}
                />
              </div>
              <h1 className="text-[40px] font-bold leading-[1.15] tracking-[-0.01em] text-[#E8E8E8]">
                {syntheticTitle}
              </h1>
              {selectedKey && renderSubpageLinks(selectedKey)}
            </div>
          ) : contentError ? (
            <div className="pt-6">
              <div className="text-[14px] text-[#909090]">Failed to load this section.</div>
              {selectedKey && <RetryButton onRetry={handleRetrySection} />}
            </div>
          ) : contentLoading || content === null || activeSection === null ? (
            <ContentSkeleton />
          ) : (
            <div className="group/page doctrine-fade-in">
              <div className="mb-2 min-h-[28px]">
                <PageIcon
                  nodeKey={activeSection.key}
                  override={iconOverride(activeSection.key)}
                  onPick={handleActiveSectionIconPick}
                  onRemove={handleActiveSectionIconRemove}
                />
              </div>
              <h1 className="text-[40px] font-bold leading-[1.15] tracking-[-0.01em] text-[#E8E8E8]">
                {activeSection.title}
              </h1>
              <div className="mb-3 mt-2 flex items-center gap-2">
                {activeSection.subtitle && <span className="text-[15px] text-[#6E6E6E]">{activeSection.subtitle}</span>}
                {!activeSection.editable && (
                  <span className="rounded-[4px] bg-[#242424] px-1.5 py-0.5 text-[11px] text-[#6E6E6E]">
                    Read-only
                  </span>
                )}
                {activeSection.systemKey?.startsWith('locked:') && (
                  <span className="rounded-[4px] bg-[#242424] px-1.5 py-0.5 text-[11px] text-[#6E6E6E]">Kept in sync with the code</span>
                )}
                {activeSection.systemKey && !activeSection.systemKey.startsWith('locked:') && (
                  <span className="rounded-[4px] bg-[#242424] px-1.5 py-0.5 text-[11px] text-[#909090]">
                    {activeSection.followsDefault ? 'Fater default' : 'Edited for your organisation'}
                  </span>
                )}
                {activeSection.systemKey && !activeSection.followsDefault && activeSection.editable && !activeSection.systemKey.startsWith('locked:') && (
                  <button type="button" onClick={handleResetToDefault}
                    className="rounded-[4px] px-1.5 py-0.5 text-[12px] text-[#8A8A8A] transition-colors hover:bg-white/5 hover:text-[#E8E8E8]">
                    Reset to default
                  </button>
                )}
                {content.document?.id && (
                  <button type="button" onClick={handleToggleHistory}
                    className={`rounded-[4px] px-1.5 py-0.5 text-[12px] transition-colors hover:bg-white/5 hover:text-[#E8E8E8] ${historyKey === activeSection.key ? 'text-[#E8E8E8]' : 'text-[#8A8A8A]'}`}>
                    History
                  </button>
                )}
                {activeSection.isSubpage && !activeSection.systemKey && (
                  <label
                    className="flex cursor-pointer items-center gap-1.5 text-[12px] text-[#8A8A8A] transition-colors hover:text-[#B5B5B5]"
                    title="When on, this page's content is added to the agents' context for this layer."
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(activeSection.contributesToContext)}
                      onChange={handleToggleContributesChange}
                      className="h-3 w-3 accent-[#8FB4E8]"
                    />
                    Include in agent context
                  </label>
                )}
              </div>

              {historyKey === activeSection.key && (
                <DoctrinePageHistory sectionKey={activeSection.key} systemPage={Boolean(activeSection.systemKey)}
                  canRestore={activeSection.editable} onRestored={handlePageRewritten} />
              )}

              {/* Subpages listed at the TOP of the page (not floating below an
                  empty editor), so a parent page never reads as blank. */}
              {renderSubpageLinks(activeSection.key)}

              <div className="mt-5">
                {activeSection.contentType === 'memory' && content.memory ? (
                  <MemoryPane initial={content.memory} />
                ) : content.document ? (
                  // Version in the key: an agent write (doctrine:updated → refetch)
                  // remounts the editor with the fresh content; local typing never
                  // refetches, so the draft is not disturbed mid-edit.
                  <DocumentPane
                    key={`${activeSection.key}:v${content.document.version}`}
                    section={activeSection}
                    document={content.document}
                    onFirstEdit={refreshManifest}
                  />
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
